'use client'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { BookOpen, ChevronRight, ChevronsUpDown, FileText, FolderTree, Link2 } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { ThemeSwitch, useConfig } from 'nextra-theme-docs'
import { useFSRoute } from 'nextra/hooks'
import { useEffect, useState } from 'react'
import { getSidebarSectionForPath, SIDEBAR_SECTION_PATHS, SIDEBAR_SECTIONS } from '../constants'

function isRouteActive(currentRoute, route) {
  if (!route) return false
  return [currentRoute, `${currentRoute}/`].includes(`${route}/`)
}

function isRouteInside(currentRoute, route) {
  if (!route) return false
  return currentRoute === route || currentRoute.startsWith(`${route}/`)
}

function isVisibleItem(item) {
  return item?.display !== 'hidden'
}

function isDirectChildRoute(parentRoute, childRoute) {
  if (!parentRoute || !childRoute || !childRoute.startsWith(`${parentRoute}/`)) return false
  const remainder = childRoute.slice(parentRoute.length + 1)
  return remainder.length > 0 && !remainder.includes('/')
}

function normalizeSidebarItems(items, skipInferForRoute = null) {
  const visibleItems = (items || []).filter(isVisibleItem)
  const consumedRoutes = new Set()
  const normalized = []

  for (const item of visibleItems) {
    const itemRoute = item?.route
    if (itemRoute && consumedRoutes.has(itemRoute)) continue

    const existingChildren = item.children?.length ? normalizeSidebarItems(item.children) : []

    const inferredChildren =
      itemRoute && itemRoute !== skipInferForRoute
        ? normalizeSidebarItems(visibleItems.filter((candidate) => isDirectChildRoute(itemRoute, candidate?.route)))
        : []

    for (const child of inferredChildren) {
      if (child?.route) consumedRoutes.add(child.route)
    }

    normalized.push({
      ...item,
      children: [...existingChildren, ...inferredChildren],
    })
  }

  return normalized
}

function findSectionNode(items, sectionPath) {
  for (const item of items || []) {
    if (!item || !isVisibleItem(item)) continue
    if (item.route === sectionPath) return item
    if (item.children?.length) {
      const nested = findSectionNode(item.children, sectionPath)
      if (nested) return nested
    }
  }
  return null
}

function getItemHref(item) {
  return item?.href || item?.route || null
}

function isExternalHref(href) {
  return /^https?:\/\//i.test(href || '')
}

function getItemIcon(item) {
  const label = `${item?.title || item?.name || ''}`.toLowerCase()
  if (item?.children?.filter(isVisibleItem).length) return FolderTree
  if (label.includes('link')) return Link2
  if (label.includes('Overview') || label.includes('getting started')) return BookOpen
  return FileText
}

function SidebarStateSync() {
  const { state } = useSidebar()

  useEffect(() => {
    document.body.dataset.docsShell = 'true'
    document.body.dataset.docsSidebar = state
    return () => {
      delete document.body.dataset.docsShell
      delete document.body.dataset.docsSidebar
    }
  }, [state])

  return null
}

// function SidebarBrand() {
//   const pathname = usePathname()
//   const { state } = useSidebar()
//   const { resolvedTheme } = useTheme()
//   const [mounted, setMounted] = useState(false)
//   useEffect(() => setMounted(true), [])
//   const isCollapsed = state === 'collapsed'
//   const supraFullLogo = mounted && resolvedTheme === 'light' ? SUPRA_LOGO_LIGHT : SUPRA_LOGO_DARK
//   const supraSmallLogo = SUPRA_LOGO_SMALL_ICON

//   const starKeyFullLogo = mounted && resolvedTheme === 'light' ? STARKEY_LOGO_DARK : STARKEY_LOGO_LIGHT
//   const starKeySmallLogo = resolvedTheme === 'light' ? STARKEY_LOGO_SMALL_DARK : STARKEY_LOGO_SMALL_LIGHT

//   const brandFullLogo = pathname?.startsWith('/starKey-wallet') ? starKeyFullLogo : supraFullLogo
//   const brandSmallLogo = pathname?.startsWith('/starKey-wallet') ? starKeySmallLogo : supraSmallLogo

//   return (
//     <Link
//       href={DEFAULT_ROUTE}
//       className={cn('inline-flex items-center gap-3 min-w-0', isCollapsed && 'justify-center')}
//       prefetch={false}
//       aria-label={SITE_NAME}
//     >
//       {isCollapsed ? (
//         <Image src={brandSmallLogo} alt={SITE_NAME} width={32} height={32} priority className="shrink-0 rounded-full" />
//       ) : (
//         <Image src={brandFullLogo} alt={SITE_NAME} width={132} height={32} priority className="w-auto h-[1.8rem]" />
//       )}
//     </Link>
//   )
// }

function SidebarSectionSelect({ isCollapsed }) {
  const pathname = usePathname()
  const router = useRouter()
  const currentSection = getSidebarSectionForPath(pathname)

  return (
    <div className="w-full">
      <Select value={currentSection.path} onValueChange={(value) => router.push(value)}>
        <SelectTrigger
          noIcon
          // Collapsed state hides SelectValue, leaving the trigger with only an icon — it needs a name.
          aria-label={`Documentation section: ${currentSection.label}`}
          className={cn(
            'sidebar-select-trigger',
            'min-h-10! justify-start! gap-[0.7rem]! rounded-[13px]!',
            'border-(--color-border-subtle)! bg-(--color-hover-bg)!',
            'text-(--color-text-primary)! shadow-none! cursor-pointer!',
            isCollapsed ? 'w-11! min-w-11! justify-center! px-0!' : 'w-full! px-3!',
          )}
        >
          {!isCollapsed && <SelectValue />}
          <span
            className={`inline-flex items-center justify-center w-4 h-4 text-(--color-text-secondary) shrink-0 [&_svg]:w-[0.95rem] [&_svg]:h-[0.95rem] ${isCollapsed ? '' : 'ml-auto'}`}
          >
            <ChevronsUpDown />
          </span>
        </SelectTrigger>
        <SelectContent
          className="sidebar-select-content min-w-52 p-[0.35rem]! rounded-[14px]! border border-(--color-border-subtle)! bg-(--color-surface-card)! shadow-[0_18px_40px_rgba(0,0,0,0.4)]!"
          position="popper"
        >
          {SIDEBAR_SECTIONS.map((section) => (
            <SelectItem
              key={section.path}
              value={section.path}
              className="min-h-9 py-2 pl-3 rounded-[10px] text-sm! cursor-pointer! mb-[0.28rem] last:mb-0 transition-colors duration-[0.18s] data-highlighted:bg-(--color-hover-bg-strong)! data-highlighted:text-(--color-text-primary)! data-[state=checked]:bg-(--color-active-bg)! data-[state=checked]:text-(--color-text-primary)!"
            >
              {section.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function SidebarNestedLeaf({ href, active, title }) {
  const external = isExternalHref(href)
  const { isMobile, setOpenMobile } = useSidebar()

  function handleClick() {
    if (isMobile) setOpenMobile(false)
  }

  return (
    <li className="list-none">
      {external ? (
        <a
          href={href || '#'}
          target="_blank"
          rel="noreferrer"
          onClick={handleClick}
          className={cn(
            'flex w-full items-center justify-between gap-2 min-h-8 rounded-md px-[0.65rem] py-[0.35rem]',
            'border-0 bg-transparent text-(--color-text-secondary) no-underline text-left cursor-pointer',
            'transition-[background-color,color] duration-[0.16s]',
            'hover:bg-(--color-hover-bg) hover:text-(--color-text-primary)',
            active && 'bg-(--color-active-bg)! text-(--color-active-text)!',
          )}
        >
          <span>{title}</span>
          <span className="text-xs">↗</span>
        </a>
      ) : (
        <Link
          href={href || '#'}
          prefetch={false}
          onClick={handleClick}
          className={cn(
            'flex w-full items-center min-h-8 rounded-md px-[0.65rem] py-[0.35rem]',
            'border-0 bg-transparent text-(--color-text-secondary) no-underline text-left cursor-pointer',
            'transition-[background-color,color] duration-[0.16s]',
            'hover:bg-(--color-hover-bg) hover:text-(--color-text-primary)',
            active && 'bg-(--color-active-bg)! text-(--supra-red-accent)! border-l-[0.1875rem] border-(--supra-red)!',
          )}
        >
          <span>{title}</span>
        </Link>
      )}
    </li>
  )
}

function SidebarNestedBranch({ branchHref, title, active, activeInside, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen)

  useEffect(() => {
    setOpen(defaultOpen)
  }, [defaultOpen])

  return (
    <li className="list-none">
      <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
        <CollapsibleTrigger asChild>
          <Link
            href={branchHref}
            className={cn(
              'flex w-full items-center justify-between min-h-8 rounded-md px-[0.65rem] py-[0.35rem]',
              'border-0 bg-transparent text-(--color-text-secondary) text-left cursor-pointer',
              'transition-[background-color,color] duration-[0.16s]',
              'hover:bg-(--color-hover-bg) hover:text-(--color-text-primary)',
              (active || activeInside) && 'bg-(--color-active-bg)! text-(--color-active-text)!',
            )}
            aria-label={`Toggle ${title}`}
            aria-expanded={open}
          >
            <span>{title}</span>
            <ChevronRight
              className={cn(
                'w-[0.95rem] h-[0.95rem] shrink-0 text-(--color-text-muted)',
                'transition-transform duration-200',
                open ? 'rotate-90' : 'rotate-0',
              )}
            />
          </Link>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-[0.1rem]">{children}</CollapsibleContent>
      </Collapsible>
    </li>
  )
}

function SidebarLeaf({ item, currentRoute, level = 0 }) {
  const href = getItemHref(item)
  const external = isExternalHref(href)
  const active = external ? false : isRouteActive(currentRoute, href)
  const title = item.title || item.name
  const Icon = getItemIcon(item)
  const { isMobile, setOpenMobile } = useSidebar()

  function handleClick() {
    if (isMobile) setOpenMobile(false)
  }

  if (level === 0) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={active}
          tooltip={title}
          className={`min-h-[2.35rem]! h-auto! mx-auto rounded-md! px-[0.8rem]! text-(--color-text-secondary)! data-[active=true]:bg-(--color-active-bg)! data-[active=true]:text-(--supra-red-accent)! whitespace-normal! ${active && 'border-l-2 border-(--supra-red)!'}`}
        >
          {external ? (
            <Link href={href || '#'} target="_blank" rel="noreferrer" onClick={handleClick} className="w-full">
              <Icon className="w-4 h-4 shrink-0 text-(--color-text-muted) in-[.data-active]:text-current" />
              <span className="flex items-center justify-between gap-2 w-full">
                <span className="min-w-0 whitespace-normal">{title}</span>
                <span className="text-supra-red-accent pt-1">↗</span>
              </span>
            </Link>
          ) : (
            <Link href={href || '#'} prefetch={false} onClick={handleClick}>
              <Icon className="w-4 h-4 shrink-0 text-(--color-text-muted) in-[.data-active]:text-current" />
              <span className="min-w-0 whitespace-normal!">{title}</span>
            </Link>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  return <SidebarNestedLeaf href={href} active={active} title={title} />
}

function SidebarBranch({ item, currentRoute, level = 0, collapsed = false }) {
  const href = getItemHref(item)
  const title = item.title || item.name
  const children = (item.children || []).filter(isVisibleItem)
  const branchHref = href || getItemHref(children[0])
  const active = isRouteActive(currentRoute, href)
  const activeInside =
    isRouteInside(currentRoute, href) || children.some((child) => isRouteInside(currentRoute, child.route))
  const defaultOpen = active || activeInside
  const Icon = getItemIcon(item)
  const [open, setOpen] = useState(defaultOpen)

  useEffect(() => {
    setOpen(defaultOpen)
  }, [defaultOpen])

  useEffect(() => {
    if (collapsed && open) setOpen(false)
  }, [collapsed, open])

  if (!children.length) {
    return <SidebarLeaf item={item} currentRoute={currentRoute} level={level} />
  }

  if (collapsed && level === 0) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={active || activeInside}
          tooltip={title}
          className={`min-h-[2.35rem]! mx-auto h-auto! rounded-md! px-0! justify-center! text-(--color-text-secondary)! data-[active=true]:bg-(--color-active-bg)! data-[active=true]:text-(--supra-red-accent)! whitespace-normal! ${active && 'border-l-2 border-(--supra-red)!'}`}
        >
          <Link href={branchHref || '#'} prefetch={false}>
            <Icon className="w-4 h-4 shrink-0 text-(--color-text-muted)" />
            <span className="min-w-0 whitespace-normal">{title}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  if (level === 0) {
    return (
      <SidebarMenuItem>
        <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
          <CollapsibleTrigger asChild>
            <Link
              href={branchHref}
              className={cn(
                'flex! w-full items-center h-auto! justify-start gap-[0.65rem] hover:bg-sidebar-accent',
                'min-h-[2.35rem]! rounded-md! px-[0.8rem]!',
                'border-0 bg-transparent text-(--color-text-secondary)! text-left cursor-pointer',
                (active || activeInside) && 'bg-(--color-active-bg)! text-(--color-active-text)!',
              )}
              data-active={active || activeInside ? 'true' : 'false'}
              aria-label={`Toggle ${title}`}
              aria-expanded={open}
            >
              <Icon className="w-4 h-4 shrink-0 text-(--color-text-muted)" />
              <span className="min-w-0 whitespace-normal! flex-1 text-left">{title}</span>
              <ChevronRight
                className={cn(
                  'ml-auto w-[0.95rem] h-[0.95rem] shrink-0 text-(--color-primary)',
                  'transition-transform duration-200',
                  open ? 'rotate-90' : 'rotate-0',
                )}
              />
            </Link>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarTree items={children} currentRoute={currentRoute} level={level + 1} collapsed={collapsed} />
          </CollapsibleContent>
        </Collapsible>
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarNestedBranch
      branchHref={branchHref}
      title={title}
      active={active}
      activeInside={activeInside}
      defaultOpen={defaultOpen}
    >
      <SidebarTree items={children} currentRoute={currentRoute} level={level + 1} collapsed={collapsed} />
    </SidebarNestedBranch>
  )
}

function SidebarTree({ items, currentRoute, level = 0, collapsed = false }) {
  const visibleItems = (items || []).filter(isVisibleItem)
  if (!visibleItems.length) return null

  const renderedItems = visibleItems.map((item) => {
    if (item.type === 'separator') {
      const separatorTitle = item.title || item.name
      return level === 0 ? (
        <li key={`${separatorTitle}-${level}`} className="list-none">
          <SidebarGroupLabel className="px-2 pb-[0.7rem]! text-[0.72rem]! font-bold! tracking-[0.08em] uppercase text-(--color-foreground)!">
            {separatorTitle}
          </SidebarGroupLabel>
        </li>
      ) : (
        <li
          key={`${separatorTitle}-${level}`}
          className="list-none px-[0.7rem] py-[0.35rem] text-[0.7rem] uppercase tracking-[0.08em] text-(--color-text-faint)"
        >
          {separatorTitle}
        </li>
      )
    }

    if (item.children?.filter(isVisibleItem).length) {
      return (
        <SidebarBranch
          key={item.route || item.name}
          item={item}
          currentRoute={currentRoute}
          level={level}
          collapsed={collapsed}
        />
      )
    }

    return <SidebarLeaf key={item.route || item.name} item={item} currentRoute={currentRoute} level={level} />
  })

  if (level === 0) {
    return <SidebarMenu>{renderedItems}</SidebarMenu>
  }

  return (
    <ul className="mt-[0.3rem] ml-3 pl-3 border-l border-(--color-border-default) list-none" data-level={level}>
      {renderedItems}
    </ul>
  )
}

export function DocsSidebar() {
  const {
    normalizePagesResult: { docsDirectories },
    hideSidebar,
  } = useConfig()
  const { state } = useSidebar()
  const currentRoute = useFSRoute().split('#', 1)[0]
  const pathname = usePathname()

  if (hideSidebar) return null

  const isCollapsed = state === 'collapsed'
  const currentSection = getSidebarSectionForPath(pathname)
  const shouldShowSectionSelect = SIDEBAR_SECTION_PATHS.some((path) => pathname?.startsWith(path))
  const sectionNode = findSectionNode(docsDirectories, currentSection.path)
  // For sections not in SIDEBAR_SECTIONS (e.g. supranova), the root index.mdx has the same
  // route as the section root. Without skipInferForRoute, normalizeSidebarItems would infer
  // all sibling pages as children of that index item, rendering it as a dropdown instead of
  // a plain link. Passing the root index route prevents that inference.
  const skipRoute = sectionNode?.route ?? docsDirectories.find((item) => item.name === 'index')?.route
  const navigationItems = normalizeSidebarItems(
    sectionNode?.children?.length ? sectionNode.children : docsDirectories,
    skipRoute,
  )
  const sectionTitle = sectionNode?.title || currentSection.label

  return (
    <Sidebar collapsible="icon" className="docs-sidebar p-0! z-30! border-0">
      <SidebarStateSync />

      {/* Header */}
      {shouldShowSectionSelect && (
        <SidebarHeader className={cn('gap-[0.85rem]! md:pt-6! pt-10! pb-0!', isCollapsed ? 'px-[0.55rem]!' : 'px-4!')}>
          {/* <div className={cn('flex items-center gap-3', isCollapsed ? 'flex-col' : 'justify-between')}>
          <SidebarBrand />
          <SidebarTrigger className="z-10 shrink-0 border border-[var(--color-border-subtle)]! bg-[var(--color-hover-bg)]! text-[var(--color-foreground)]!" />
        </div> */}
          {shouldShowSectionSelect && <SidebarSectionSelect isCollapsed={isCollapsed} />}
        </SidebarHeader>
      )}

      {/* Main content */}
      <SidebarContent className={cn('py-1', isCollapsed ? 'px-[0.45rem]' : 'px-[0.6rem] pb-[0.8rem]')}>
        <ScrollArea className="h-full **:data-[slot=scroll-area-viewport]:pr-[0.15rem] **:data-[slot=scroll-area-scrollbar]:w-[0.65rem] **:data-[slot=scroll-area-scrollbar]:p-[0.1rem] **:data-[slot=scroll-area-thumb]:bg-(--color-scrollbar-thumb) [&>div]:block!">
          <SidebarGroup className="p-[0.35rem]! px-[0.4rem]!">
            {!isCollapsed && (
              <SidebarGroupLabel className="px-2 pb-[0.7rem]! text-[0.72rem]! font-bold! tracking-[0.08em] uppercase text-(--color-foreground)!">
                {sectionTitle}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent className={isCollapsed ? 'overflow-hidden!' : ''}>
              <SidebarTree items={navigationItems} currentRoute={currentRoute} collapsed={isCollapsed} />
            </SidebarGroupContent>
          </SidebarGroup>
        </ScrollArea>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter
        className={cn(
          'border-t border-(--color-border-faint)',
          isCollapsed ? 'p-[0.75rem_0.45rem]! items-center' : 'p-[0.75rem_1rem]!',
        )}
      >
        <ThemeSwitch
          lite={isCollapsed}
          className="w-full text-(--color-text-secondary)! [&_select]:bg-transparent! [&_select]:border! [&_select]:border-(--color-border-default)! [&_select]:rounded-lg! [&_select]:text-[0.85rem]! [&_select]:px-2! [&_select]:py-1! [&_select]:cursor-pointer! [&_select]:flex-1 [&_select]:text-inherit!"
        />
      </SidebarFooter>
    </Sidebar>
  )
}

export function DocsSidebarMobileBar() {
  const { hideSidebar } = useConfig()
  if (hideSidebar) return null
  return <></>
}
